import React, { useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import MomentDatePicker from "../../../../../components/inputs/MomentDatePicker";
import { errorText, inr, payrollApi } from "./payrollApi";
import { card, centeredModal, GAP, iconButton, Toolbar } from "./layout";

const TYPES = [
  { value: "bonus", label: "Bonus", color: "green" },
  { value: "overtime", label: "Extra overtime", color: "cyan" },
  { value: "deduction", label: "Deduction", color: "red" },
  { value: "advance", label: "Advance (recovered over months)", color: "orange" },
];
const TYPE_BY_VALUE = Object.fromEntries(TYPES.map((t) => [t.value, t]));

const Adjustments = () => {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(moment().startOf("month"));
  const monthKey = month.format("YYYY-MM");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery(
    ["payroll-adjustments", monthKey],
    () => payrollApi.adjustments(monthKey)
  );
  const { data: staff = [] } = useQuery(["payroll-staff", "all"], () =>
    payrollApi.staff()
  );

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ type: "bonus", month, recovery_months: 1 });
    setOpen(true);
  };

  const save = async () => {
    const v = await form.validateFields();
    try {
      setSaving(true);
      await payrollApi.createAdjustment({
        user: v.user,
        type: v.type,
        amount: v.amount,
        month: v.month.format("YYYY-MM"),
        recovery_months: v.type === "advance" ? v.recovery_months : undefined,
        reason: v.reason,
      });
      message.success("Saved");
      setOpen(false);
      queryClient.invalidateQueries("payroll-adjustments");
      queryClient.invalidateQueries("payroll-run");
    } catch (err) {
      message.error(errorText(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await payrollApi.deleteAdjustment(id);
      message.success("Removed");
      queryClient.invalidateQueries("payroll-adjustments");
    } catch (err) {
      message.error(errorText(err, "Could not remove"));
    }
  };

  const columns = [
    { title: "Staff", dataIndex: "staff_name" },
    {
      title: "Type",
      dataIndex: "type",
      render: (v) => <Tag color={TYPE_BY_VALUE[v]?.color}>{TYPE_BY_VALUE[v]?.label || v}</Tag>,
    },
    { title: "Amount", dataIndex: "amount", align: "right", render: inr },
    {
      title: "Applies",
      render: (_, r) =>
        r.type === "advance" && r.recovery_months > 1
          ? `${moment(r.month).format("MMM YYYY")} · ${r.recovery_months} months`
          : moment(r.month).format("MMM YYYY"),
    },
    { title: "Reason", dataIndex: "reason" },
    {
      title: "",
      align: "right",
      render: (_, r) => (
        <Popconfirm title="Remove this item?" onConfirm={() => remove(r._id)}>
          <Button size="small" danger>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Toolbar
        left={
          <MomentDatePicker
            picker="month"
            allowClear={false}
            value={month}
            onChange={(d) => d && setMonth(d.startOf("month"))}
          />
        }
        right={
          <Button type="primary" style={iconButton} onClick={openModal}>
            <PlusOutlined />
            <span>Add item</span>
          </Button>
        }
      />

      <Alert
        style={{ marginBottom: GAP.lg }}
        type="info"
        showIcon
        message="These feed into the month's payroll when it is generated. Regenerate a draft payroll to pick up new items."
      />

      <div style={{ ...card, padding: GAP.sm }}>
        <Table
          rowKey="_id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        {...centeredModal}
        open={open}
        title="Add bonus, overtime, deduction or advance"
        onCancel={() => setOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="user" label="Staff" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={staff.map((s) => ({ value: s.user_id, label: s.name }))}
              placeholder="Select staff"
            />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES} />
          </Form.Item>
          <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
            <InputNumber min={0.01} className="w-full" />
          </Form.Item>
          <Form.Item name="month" label="Month" rules={[{ required: true }]}>
            <MomentDatePicker picker="month" className="w-full" allowClear={false} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(a, b) => a.type !== b.type}
          >
            {({ getFieldValue }) =>
              getFieldValue("type") === "advance" ? (
                <Form.Item
                  name="recovery_months"
                  label="Recover over (months)"
                  extra="Starts in the month above, in equal instalments."
                >
                  <InputNumber min={1} max={36} className="w-full" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Adjustments;
